import axios from 'axios';
import { Globals } from '@/modules/Globals';
import { ICoupon } from '@/interfaces/ICoupon';
import { IDataOptions } from '@/interfaces/IDataOptions';
import { PaymentMethod } from '@/enums/PaymentMethod';
import { OrderType } from '@/enums/OrderEnums';

const apiUrl = Globals.apiPath;

interface CreateCouponDto {
  code: string;
  name: string;
  description?: string;
  isActive?: boolean;
  isPercentage: boolean;
  value: number;
  maxDiscountAmount?: number | null;
  validFrom?: string | null;
  validUntil?: string | null;
  maxUses?: number | null;
  maxUsesPerCustomer?: number | null;
  minOrderAmount?: number | null;
  maxOrderAmount?: number | null;
  allowedPaymentMethods?: number | null;
  allowedOrderTypes?: OrderType[] | null;
  allowedDays?: number | null;
  allowedHours?: Array<{ from: string; to: string }> | null;
  metadata?: Record<string, any> | null;
}

interface UpdateCouponDto extends Partial<CreateCouponDto> {}

interface CouponListResponse {
  items: ICoupon[];
  total: number;
  page: number;
  take?: number;
  validCount: number;
  expiredCount: number;
}

interface CouponUse {
  id: number;
  couponId: number;
  orderId: number;
  customerId: number | null;
  userId: number | null;
  discountAmount: number;
  usedAt: Date;
  customer?: any;
  user?: any;
  order?: any;
}

interface CouponUsageResponse {
  items: CouponUse[];
  total: number;
  page: number;
  take?: number;
}

export default class CouponsService {
  static async list(
    restaurantId: number,
    branchId: number,
    options?: IDataOptions & { status?: 'valid' | 'expired' | 'all' }
  ): Promise<CouponListResponse> {
    const params: any = {};

    if (options) {
      const { status, ...dataOptions } = options;

      if (status) {
        params.status = status;
      }

      if (Object.keys(dataOptions).length > 0) {
        params.options = JSON.stringify(dataOptions);
      }
    }

    const response = await axios.get(
      `${apiUrl}/restaurants/${restaurantId}/branches/${branchId}/coupons`,
      { params }
    );

    return {
      items: response.data.items || response.data.data || response.data,
      total: response.data.total || response.data.length,
      page: response.data.page || 1,
      take: response.data.take,
      validCount: response.data.validCount || 0,
      expiredCount: response.data.expiredCount || 0,
    };
  }

  static async getOne(
    restaurantId: number,
    branchId: number,
    couponId: number
  ): Promise<ICoupon> {
    const response = await axios.get(
      `${apiUrl}/restaurants/${restaurantId}/branches/${branchId}/coupons/${couponId}`
    );
    return response.data.data || response.data;
  }

  static async create(
    restaurantId: number,
    branchId: number,
    data: CreateCouponDto
  ): Promise<ICoupon> {
    const response = await axios.post(
      `${apiUrl}/restaurants/${restaurantId}/branches/${branchId}/coupons`,
      data
    );
    return response.data.data || response.data;
  }

  static async update(
    restaurantId: number,
    branchId: number,
    couponId: number,
    data: UpdateCouponDto
  ): Promise<ICoupon> {
    const response = await axios.put(
      `${apiUrl}/restaurants/${restaurantId}/branches/${branchId}/coupons/${couponId}`,
      data
    );
    return response.data.data || response.data;
  }

  static async delete(
    restaurantId: number,
    branchId: number,
    couponId: number
  ): Promise<void> {
    await axios.delete(
      `${apiUrl}/restaurants/${restaurantId}/branches/${branchId}/coupons/${couponId}`
    );
  }

  static async getUsageHistory(
    restaurantId: number,
    branchId: number,
    couponId: number,
    options?: IDataOptions
  ): Promise<CouponUsageResponse> {
    const params: any = {};

    if (options && Object.keys(options).length > 0) {
      params.options = JSON.stringify(options);
    }

    const response = await axios.get(
      `${apiUrl}/restaurants/${restaurantId}/branches/${branchId}/coupons/${couponId}/uses`,
      { params }
    );

    return {
      items: response.data.items || response.data.data || response.data,
      total: response.data.total || response.data.length,
      page: response.data.page || 1,
      take: response.data.take,
    };
  }

  static async validateForOrder(
    restaurantId: number,
    branchId: number,
    orderId: number,
    code: string
  ): Promise<{
    valid: boolean;
    error?: string;
    coupon?: ICoupon;
    discountAmount?: number;
  }> {
    const response = await axios.post(
      `${apiUrl}/restaurants/${restaurantId}/branches/${branchId}/coupons/orders/${orderId}/validate`,
      { code }
    );
    return response.data;
  }

  static async applyToOrder(
    restaurantId: number,
    branchId: number,
    orderId: number,
    code: string
  ): Promise<any> {
    const response = await axios.post(
      `${apiUrl}/restaurants/${restaurantId}/branches/${branchId}/coupons/orders/${orderId}/apply`,
      { code }
    );
    return response.data.data || response.data;
  }

  // Helper to convert payment methods array to bitmask
  static paymentMethodsToBitmask(methods: PaymentMethod[]): number {
    return methods.reduce((mask, method) => mask | method, 0);
  }

  // Helper to convert bitmask to payment methods array
  static bitmaskToPaymentMethods(mask: number): PaymentMethod[] {
    const methods: PaymentMethod[] = [];
    Object.values(PaymentMethod).forEach((method) => {
      if (typeof method === 'number' && mask & method) {
        methods.push(method);
      }
    });
    return methods;
  }
}
